import { useEffect, useRef, useState } from "react";

/**
 * Right-side floating section indicator.
 *
 *  - One dot per provided section (active dot grows + glows)
 *  - Hover reveals the label to the left
 *  - Click smooth-scrolls to the target section
 *  - Auto-detects whether the area behind the dots is light or dark
 *    and inverts the dot colour so the strip is always visible.
 */
export type DotSection = { id: string; label: string };

interface SectionDotsProps {
  sections: DotSection[];
  /**
   * Optional CSS selector for the scroll container. If omitted, the component
   * auto-detects the nearest scrollable ancestor of the first matched section
   * (falling back to the viewport).
   */
  scrollerSelector?: string;
}

/** Heuristic: is this CSS color string a "light" colour (perceived luminance > ~0.6)? */
const isLightColor = (rgb: string): boolean => {
  const m = rgb.match(/\d+(\.\d+)?/g);
  if (!m || m.length < 3) return false;
  const [r, g, b, a] = m.map(Number);
  if (a !== undefined && a === 0) return false;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6;
};

const SectionDots = ({ sections, scrollerSelector }: SectionDotsProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  const navRef = useRef<HTMLElement | null>(null);

  /* Active section tracking — re-attaches when lazy sections mount */
  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    const observed = new Set<Element>();

    const findScroller = (firstEl: HTMLElement): Element | null => {
      if (scrollerSelector) return document.querySelector(scrollerSelector);
      let node: HTMLElement | null = firstEl.parentElement;
      while (node && node !== document.body) {
        const cs = getComputedStyle(node);
        if (
          (cs.overflowY === "auto" || cs.overflowY === "scroll") &&
          node.scrollHeight > node.clientHeight + 4
        ) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    };

    const attach = () => {
      const els = sections
        .map((s) => document.getElementById(s.id))
        .filter((el): el is HTMLElement => el !== null);
      if (els.length === 0) return;

      if (!observer) {
        const root = findScroller(els[0]);
        observer = new IntersectionObserver(
          (entries) => {
            // Pick the entry with the highest intersection ratio that is intersecting
            let best: IntersectionObserverEntry | null = null;
            entries.forEach((entry) => {
              if (entry.isIntersecting && (!best || entry.intersectionRatio > best.intersectionRatio)) {
                best = entry;
              }
            });
            if (best && best.intersectionRatio >= 0.4) {
              const idx = sections.findIndex((s) => s.id === best!.target.id);
              if (idx >= 0) setActiveIndex(idx);
            }
          },
          { threshold: [0.2, 0.4, 0.6, 0.8], root }
        );
      }

      els.forEach((el) => {
        if (!observed.has(el)) {
          observer!.observe(el);
          observed.add(el);
        }
      });
    };

    attach();

    // Watch the DOM for lazy-mounted sections and re-attach
    const mo = new MutationObserver(() => {
      if (observed.size < sections.length) attach();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      observer?.disconnect();
    };
  }, [sections, scrollerSelector]);

  /* Background-aware theme: sample what's painted behind the dot strip. */
  useEffect(() => {
    let raf = 0;
    const sample = () => {
      const nav = navRef.current;
      if (!nav) return;
      const rect = nav.getBoundingClientRect();
      const x = Math.max(rect.left - 12, 4);
      const y = rect.top + rect.height / 2;

      const prevPE = nav.style.pointerEvents;
      nav.style.pointerEvents = "none";
      let el: Element | null = document.elementFromPoint(x, y);
      nav.style.pointerEvents = prevPE;

      while (el && el !== document.documentElement) {
        const bg = getComputedStyle(el).backgroundColor;
        if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") {
          setTheme(isLightColor(bg) ? "light" : "dark");
          return;
        }
        el = el.parentElement;
      }
      const bodyBg = getComputedStyle(document.body).backgroundColor;
      setTheme(isLightColor(bodyBg) ? "light" : "dark");
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(sample);
    };

    sample();
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll, { capture: true } as EventListenerOptions);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const scrollToSection = (id: string) => {
    import("@/lib/smoothScroll").then((m) => m.scrollToHash(id));
  };

  const isLight = theme === "light";
  const activeDot = isLight
    ? "h-3 w-3 bg-charcoal shadow-[0_0_10px_rgba(26,26,26,0.35)]"
    : "h-3 w-3 bg-stone shadow-[0_0_12px_rgba(255,255,255,0.6)]";
  const idleDot = isLight
    ? "h-1.5 w-1.5 bg-charcoal/40 group-hover:bg-charcoal/80"
    : "h-1.5 w-1.5 bg-stone/40 group-hover:bg-stone/80";
  const labelColor = isLight ? "text-charcoal/70" : "text-stone/70";

  return (
    <nav
      ref={navRef}
      aria-label="Section navigation"
      className="hidden md:flex fixed right-6 lg:right-8 top-1/2 -translate-y-1/2 z-40 flex-col items-center gap-1"
    >
      {sections.map((s, i) => {
        const isActive = i === activeIndex;
        return (
          <button
            key={s.id}
            onClick={() => scrollToSection(s.id)}
            aria-label={`Go to ${s.label}`}
            className="group relative flex h-6 w-6 items-center justify-center"
          >
            <span
              className={`pointer-events-none absolute right-full mr-3 whitespace-nowrap font-sans font-light text-[10px] tracking-[0.25em] uppercase transition-opacity duration-300 ${labelColor} ${
                isActive ? "opacity-0" : "opacity-0 group-hover:opacity-100"
              }`}
            >
              {s.label}
            </span>
            <span
              className={`block rounded-full transition-all duration-500 ${
                isActive ? activeDot : idleDot
              }`}
            />
          </button>
        );
      })}
    </nav>
  );
};

export default SectionDots;
